/**
 * Bhabhi (Thulla) — server-authoritative rules engine (South Asian shedding game, 4 players).
 *
 * Rules implemented:
 *  - One 52-card deck dealt out, 13 each. The holder of the ace of spades leads the first round and must play it.
 *  - A round: starting with the leader, each player who still has cards plays one card in turn and must follow the
 *    led suit when able. Ace is high.
 *  - If everyone follows suit, the cards are discarded for good and the player with the highest card of the led suit
 *    leads the next round.
 *  - A player who cannot follow suit plays any card (a "thulla") and the round stops at once: whoever played the
 *    highest card of the led suit must pick up every card on the table and leads the next round.
 *  - A player whose hand is empty (and who did not just pick cards up) is out. The game ends when one player is left
 *    holding cards: that player is the Bhabhi and finishes last. Players finish in the order they went out.
 *  - A hard cap on rounds ends a very long game, ranking the players left by how few cards they hold.
 */
import { buildDeck, parseCard, removeCards, sortHand, type CardId, type Suit } from '../../core/cards.js';
import { checkInt, mergeKnown } from '../../core/config.js';
import { shuffle } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  type EngineEvent,
  type GameEngine,
  type Standing,
} from '../../core/types.js';

export interface BhabhiConfig {
  /** Hard cap on rounds so a game always terminates. */
  maxTricks: number;
  trickEndDelayMs: number;
}

export type BhabhiPhase = 'PLAYING' | 'TRICK_END' | 'MATCH_END';

export interface BhabhiPlay {
  seat: number;
  card: CardId;
}

export interface BhabhiTrick {
  plays: BhabhiPlay[];
  outcome: 'discard' | 'pickup';
  /** Seat that won the round (leads next) — also the one who picked the cards up on a thulla. */
  taker: number;
  /** Seat that played off-suit, ending the round (pickup only). */
  thulla: number | null;
}

export interface BhabhiState {
  config: BhabhiConfig;
  players: string[];
  phase: BhabhiPhase;
  hands: CardId[][];
  /** Cards played in the round in progress, in order. */
  pile: BhabhiPlay[];
  /** Seats due to play this round, in order. */
  order: number[];
  leader: number;
  turn: number;
  trickNo: number;
  discarded: number;
  /** Seats in the order they went out. */
  out: number[];
  /** Suits each seat has been seen unable to follow. */
  voids: Suit[][];
  lastTrick: BhabhiTrick | null;
  /** Set once the game has ended: the seat left holding cards (null if everybody got out). */
  bhabhi: number | null;
}

export type BhabhiAction = { type: 'play'; card: CardId };

export interface BhabhiLegal {
  cards: CardId[];
  /** True when playing one of these cards would be a thulla (out of the led suit). */
  thulla: boolean;
}

export interface BhabhiView {
  gameType: 'BHABHI';
  config: BhabhiConfig;
  seat: number | null;
  phase: BhabhiPhase;
  hand: CardId[];
  handCounts: number[];
  pile: BhabhiPlay[];
  leadSuit: Suit | null;
  leader: number;
  turn: number;
  trickNo: number;
  discarded: number;
  out: number[];
  voids: Suit[][];
  lastTrick: BhabhiTrick | null;
  bhabhi: number | null;
  /** Cards each seat holds (so generic score UIs work). */
  totals: number[];
}

const DEFAULTS: BhabhiConfig = {
  maxTricks: 400,
  trickEndDelayMs: 1500,
};

const SEATS = 4;
const ACE_SPADES = 'AS';

/** Cards a seat may play now. */
export function bhabhiLegal(hand: readonly CardId[], pile: readonly BhabhiPlay[], firstRound: boolean): BhabhiLegal {
  if (pile.length === 0) {
    // The very first card of the game is the ace of spades.
    if (firstRound && hand.includes(ACE_SPADES)) return { cards: [ACE_SPADES], thulla: false };
    return { cards: [...hand], thulla: false };
  }
  const lead = parseCard(pile[0]!.card).suit;
  const followers = hand.filter((c) => parseCard(c).suit === lead);
  return followers.length > 0 ? { cards: followers, thulla: false } : { cards: [...hand], thulla: true };
}

/** The play that wins the round: the highest card of the led suit. */
export function bhabhiHighest(pile: readonly BhabhiPlay[]): BhabhiPlay {
  const lead = parseCard(pile[0]!.card).suit;
  return pile.filter((p) => parseCard(p.card).suit === lead).reduce((best, p) => (parseCard(p.card).rank > parseCard(best.card).rank ? p : best));
}

/** Seats with cards, clockwise from `from` (inclusive). */
function activeFrom(hands: readonly (readonly CardId[])[], from: number): number[] {
  const out: number[] = [];
  for (let i = 0; i < SEATS; i++) {
    const seat = (from + i) % SEATS;
    if (hands[seat]!.length > 0) out.push(seat);
  }
  return out;
}

function startRound(state: BhabhiState, leader: number): void {
  state.order = activeFrom(state.hands, leader);
  state.leader = state.order[0] ?? leader;
  state.turn = state.leader;
  state.pile = [];
  state.trickNo++;
}

function finish(state: BhabhiState, events: EngineEvent[]): void {
  state.phase = 'MATCH_END';
  const left = state.hands.map((h, s) => (h.length > 0 ? s : -1)).filter((s) => s >= 0);
  state.bhabhi = left.length === 1 ? left[0]! : null;
  events.push({ type: 'matchEnd', standings: standings(state), bhabhi: state.bhabhi });
}

export const bhabhiEngine: GameEngine<BhabhiConfig, BhabhiState, BhabhiAction, BhabhiView, BhabhiLegal> = {
  gameType: 'BHABHI',
  kind: 'match',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('maxTricks', c.maxTricks, 20, 2000);
    checkInt('trickEndDelayMs', c.trickEndDelayMs, 0, 10000);
    return c;
  },

  seatLimits: () => ({ min: SEATS, max: SEATS }),

  createState(config, seats, rng) {
    assertEngine(seats.length === SEATS, 'INVALID_CONFIG', 'Bhabhi needs exactly 4 seats');
    const players = Array<string>(SEATS).fill('');
    for (const s of seats) {
      assertEngine(s.seat >= 0 && s.seat < SEATS && !players[s.seat], 'SEAT_TAKEN', `Bad seat ${s.seat}`);
      players[s.seat] = s.playerId;
    }
    const deck = shuffle(buildDeck(), rng);
    const hands: CardId[][] = [[], [], [], []];
    deck.forEach((c, i) => hands[i % SEATS]!.push(c));
    const state: BhabhiState = {
      config,
      players,
      phase: 'PLAYING',
      hands: hands.map((h) => sortHand(h)),
      pile: [],
      order: [],
      leader: 0,
      turn: 0,
      trickNo: 0,
      discarded: 0,
      out: [],
      voids: [[], [], [], []],
      lastTrick: null,
      bhabhi: null,
    };
    startRound(state, state.hands.findIndex((h) => h.includes(ACE_SPADES)));
    return state;
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    if (raw.type === 'play' && typeof raw.card === 'string' && raw.card.length <= 4) return { type: 'play', card: raw.card };
    throw new EngineError('INVALID_ACTION', 'Unknown Bhabhi action');
  },

  applyAction(state, seat, action) {
    assertEngine(state.phase !== 'MATCH_END', 'GAME_OVER', 'The game is over');
    assertEngine(state.phase === 'PLAYING' && seat === state.turn, 'NOT_YOUR_TURN', 'Not your turn');
    const events: EngineEvent[] = [];
    const hand = state.hands[seat]!;
    assertEngine(hand.includes(action.card), 'INVALID_CARD', 'Card not in hand');
    const legal = bhabhiLegal(hand, state.pile, state.trickNo === 1);
    assertEngine(legal.cards.includes(action.card), 'ILLEGAL_MOVE', 'That card is not allowed now');

    removeCards(hand, [action.card]);
    state.pile.push({ seat, card: action.card });
    events.push({ type: 'cardPlayed', seat, card: action.card, thulla: legal.thulla });
    if (legal.thulla) {
      const lead = parseCard(state.pile[0]!.card).suit;
      if (!state.voids[seat]!.includes(lead)) state.voids[seat]!.push(lead);
    }

    const roundOver = legal.thulla || state.pile.length === state.order.length;
    if (!roundOver) {
      state.turn = state.order[state.pile.length]!;
      return events;
    }

    const highest = bhabhiHighest(state.pile);
    const plays = state.pile.map((p) => ({ ...p }));
    if (legal.thulla) {
      // The highest card of the led suit picks everything up.
      state.hands[highest.seat] = sortHand([...state.hands[highest.seat]!, ...plays.map((p) => p.card)]);
      state.lastTrick = { plays, outcome: 'pickup', taker: highest.seat, thulla: seat };
      events.push({ type: 'pickup', seat: highest.seat, thulla: seat, count: plays.length });
    } else {
      state.discarded += plays.length;
      state.lastTrick = { plays, outcome: 'discard', taker: highest.seat, thulla: null };
      events.push({ type: 'discard', seat: highest.seat, count: plays.length });
    }
    // Whoever played their last card and did not pick anything up is out, in the order they played.
    for (const p of plays) {
      if (state.hands[p.seat]!.length === 0 && !state.out.includes(p.seat)) {
        state.out.push(p.seat);
        events.push({ type: 'out', seat: p.seat, place: state.out.length });
      }
    }
    state.leader = highest.seat;
    state.turn = highest.seat;
    state.phase = 'TRICK_END';
    return events;
  },

  getActors(state) {
    return state.phase === 'PLAYING' ? [state.turn] : [];
  },

  getLegalActions(state, seat) {
    if (state.phase !== 'PLAYING' || seat !== state.turn) return { cards: [], thulla: false };
    return bhabhiLegal(state.hands[seat]!, state.pile, state.trickNo === 1);
  },

  getTimeoutAction(state, seat) {
    const legal = bhabhiLegal(state.hands[seat]!, state.pile, state.trickNo === 1);
    const lowest = [...legal.cards].sort((a, b) => parseCard(a).rank - parseCard(b).rank)[0]!;
    return { type: 'play', card: lowest };
  },

  getView(state, seat) {
    return {
      gameType: 'BHABHI',
      config: state.config,
      seat,
      phase: state.phase,
      hand: seat === null ? [] : [...state.hands[seat]!],
      handCounts: state.hands.map((h) => h.length),
      pile: state.phase === 'TRICK_END' ? (state.lastTrick?.plays ?? []).map((p) => ({ ...p })) : state.pile.map((p) => ({ ...p })),
      leadSuit: state.pile[0] ? parseCard(state.pile[0].card).suit : null,
      leader: state.leader,
      turn: state.turn,
      trickNo: state.trickNo,
      discarded: state.discarded,
      out: [...state.out],
      voids: state.voids.map((v) => [...v]),
      lastTrick: state.lastTrick ? { ...state.lastTrick, plays: state.lastTrick.plays.map((p) => ({ ...p })) } : null,
      bhabhi: state.bhabhi,
      totals: state.hands.map((h) => h.length),
    };
  },

  getSystemStep(state) {
    return state.phase === 'TRICK_END' ? { kind: 'collectTrick', delayMs: state.config.trickEndDelayMs } : null;
  },

  runSystemStep(state) {
    const events: EngineEvent[] = [];
    if (state.phase !== 'TRICK_END') return events;
    state.pile = [];
    const left = activeFrom(state.hands, 0);
    if (left.length <= 1 || state.trickNo >= state.config.maxTricks) {
      finish(state, events);
      return events;
    }
    // The taker leads; if they are out, the next player with cards does.
    state.phase = 'PLAYING';
    startRound(state, state.leader);
    events.push({ type: 'trickCollected', leader: state.leader });
    return events;
  },

  isFinished: (state) => state.phase === 'MATCH_END',

  getStandings(state): Standing[] {
    return standings(state);
  },

  getRoundsCompleted: (state) => state.trickNo,
};

/** Places: players in the order they went out, then whoever is left by fewest cards (the Bhabhi last). */
function standings(state: BhabhiState): Standing[] {
  const remaining = [0, 1, 2, 3].filter((s) => !state.out.includes(s)).sort((a, b) => state.hands[a]!.length - state.hands[b]!.length);
  const ranked = [...state.out, ...remaining];
  return ranked.map((seat, i) => ({ seat, place: i + 1, score: 100 - i * 10 - state.hands[seat]!.length }));
}
